import dayjs, {Dayjs, OpUnitType} from 'dayjs';
import Highcharts from 'highcharts';

import {Asset} from '@deviceinsight/ng-ui-api-client';
import {AggregatedFact} from '../../../../api/kpiApi';

import {getDateRange, getGroupBySettings, KpiGroupBy, KpiGroupByValuesType, MomentTimeUnit} from '../common/kpiUtils';

import {AggregationSettings, ChartData, ChartSettings, DateCurve, KpiWidgetItem} from '../common/types';
import {KpiAggregatedChartWidgetConfigSettings} from './types';

// chart options
const buildChartOptions = (
	groupBy: KpiGroupByValuesType,
	chartSettings: ChartSettings,
	series: Array<DateCurve>,
	stacked: boolean,
	dateFormat: string
) => ({
	chart: {},
	title: {
		text: undefined
	},
	credits: {
		enabled: false
	},
	xAxis: chartSettings.xAxis,
	yAxis: chartSettings.yAxis,
	plotOptions: {
		series: {
			animation: false,
			stacking: stacked ? 'normal' : undefined
		}
	},
	series,
	tooltip: {
		formatter: function (): string {
			// @ts-ignore
			return `<b>${formatLabel(Number(this.x), groupBy, dateFormat)}</b><br/>${this.series.name}: ${this.y} ${
				// @ts-ignore
				this.series.options.unit || ''
			}`;
		}
	},
	accessibility: {
		enabled: false
	}
});

const formatLabel = (value: number, groupBy: KpiGroupByValuesType, dateFormat: string) => {
	const formattedDate = groupBy === 'BY_WEEK' ? dayjs(value).isoWeek().toString() : dayjs(value).format(dateFormat);

	if (groupBy === KpiGroupBy.WEEK) {
		return `KW ${formattedDate}`;
	}

	return formattedDate;
};

const createYAxes = (items: Array<KpiWidgetItem>, stacked: boolean, commonYAxis: boolean) => {
	if (stacked || commonYAxis) {
		return {
			title: {
				enabled: false
			}
		};
	} else {
		return items.map((item) => ({
			title: {
				enabled: false
			},
			labels: {
				style: {
					color: item.color
				}
			}
		}));
	}
};

function createMonthsRange(fromTimestamp: number, toTimestamp: number) {
	const result = [fromTimestamp];
	let startOfNextMonth = dayjs(fromTimestamp)
		.add(1, 'month')
		.set('date', 1)
		.set('hour', 0)
		.set('minute', 0)
		.set('second', 0)
		.valueOf();
	while (startOfNextMonth < toTimestamp) {
		result.push(startOfNextMonth);
		startOfNextMonth = dayjs(startOfNextMonth).add(1, 'month').valueOf();
	}

	return result;
}

function createWeeksRange(fromTimestamp: number, toTimestamp: number) {
	const result = [fromTimestamp];
	let startOfNextWeek = dayjs(fromTimestamp)
		.add(8 - dayjs(fromTimestamp).isoWeekday(), 'days')
		.valueOf();
	while (startOfNextWeek < toTimestamp) {
		result.push(startOfNextWeek);
		startOfNextWeek = dayjs(startOfNextWeek).add(1, 'week').valueOf();
	}

	return result;
}

function createDaysRange(fromTimestamp: number, toTimestamp: number) {
	const result = [fromTimestamp];
	let startOfNextPeriod = dayjs(fromTimestamp)
		.add(1, 'day')
		.set('hour', 0)
		.set('minute', 0)
		.set('second', 0)
		.valueOf();
	while (startOfNextPeriod < toTimestamp) {
		result.push(startOfNextPeriod);
		startOfNextPeriod = dayjs(startOfNextPeriod).add(1, 'day').valueOf();
	}

	return result;
}

function createHoursRange(fromTimestamp: number, toTimestamp: number) {
	const result = [fromTimestamp];
	let startOfNextPeriod = dayjs(fromTimestamp).add(1, 'hour').set('minute', 0).set('second', 0).valueOf();
	while (startOfNextPeriod < toTimestamp) {
		result.push(startOfNextPeriod);
		startOfNextPeriod = dayjs(startOfNextPeriod).add(1, 'hour').valueOf();
	}

	return result;
}

export function createRange(fromTimestamp: number, toTimestamp: number, period: OpUnitType): number[] {
	switch (period) {
		case 'week':
			return createWeeksRange(fromTimestamp, toTimestamp);
		case 'day':
			return createDaysRange(fromTimestamp, toTimestamp);
		case 'month':
			return createMonthsRange(fromTimestamp, toTimestamp);
		case 'hour':
			return createHoursRange(fromTimestamp, toTimestamp);
		default:
			throw new Error(`Can't create range for period: ${period}`);
	}
}

export const createKpiChartCategories = (
	aggregationSettings: AggregationSettings,
	fromPointOfTime: Dayjs
): Array<number> => {
	const {start, end} = getDateRange(fromPointOfTime, aggregationSettings.query);

	if (aggregationSettings.groupBy) {
		const {momentTimeUnit} = getGroupBySettings(aggregationSettings.groupBy);
		return createRange(+start, +end, momentTimeUnit);
	} else {
		throw new Error('createKpiChartCategories: aggregationSettings.groupBy is null');
	}
};

const digestSettings = (
	settings: KpiAggregatedChartWidgetConfigSettings,
	aggregationSettings: AggregationSettings,
	dateFormat: string,
	fromPointOfTime: Dayjs
): ChartSettings => {
	const {groupBy, items, stacked, commonYAxis} = settings;
	const categories = createKpiChartCategories(aggregationSettings, fromPointOfTime);
	const yAxes = createYAxes(items, stacked, commonYAxis);
	return {
		xAxis: {
			categories,
			labels: {
				formatter: function () {
					return formatLabel(Number(this.value), groupBy, dateFormat);
				}
			}
		},
		yAxis: yAxes
	};
};

const createSeriesData = (
	facts: Array<AggregatedFact>,
	categories: Array<any>,
	groupBy: KpiGroupByValuesType
): Array<any> => {
	return categories.map((category) => {
		const fact = facts.find((fact) => {
			const factStartTs = dayjs(fact.rangeStart).valueOf();
			const factEndTs = dayjs(fact.rangeEnd).valueOf();
			let categoryDay = dayjs(Number(category));

			switch (groupBy) {
				case KpiGroupBy.DAY:
					categoryDay = categoryDay.add(12, MomentTimeUnit.HOUR);
					break;

				case KpiGroupBy.WEEK:
				case KpiGroupBy.MONTH:
					categoryDay = categoryDay.add(2, MomentTimeUnit.DAY);
					break;

				default:
					categoryDay = categoryDay.add(30, MomentTimeUnit.MINUTE);
					break;
			}

			const categoryDayTs = +categoryDay;

			return categoryDayTs >= factStartTs && categoryDayTs <= factEndTs;
		});

		if (fact && fact.aggregatedValue) {
			return fact.aggregatedValue;
		}

		return null;
	}, []);
};

const createSeries = (
	chartDataItem: ChartData,
	chartSettings: ChartSettings,
	seriesIndex: number,
	showAssetNames: boolean,
	stacked: boolean,
	commonYAxis: boolean,
	groupBy: KpiGroupByValuesType,
	assets: Asset[]
): DateCurve => {
	const {facts, label, kpiName, assetUrn, color, unit, chartType = 'column', uuid} = chartDataItem;
	const seriesName =
		label || (showAssetNames ? `${assets.find(({urn}) => assetUrn === urn)?.name || '--'} - ${kpiName}` : kpiName);
	const series: DateCurve = {
		name: seriesName,
		// @ts-ignore
		id: uuid ? uuid : seriesName,
		type: chartType as 'column',
		color,
		unit,
		data: [],
		stack: stacked ? 0 : seriesIndex,
		yAxis: 0
	};

	if (!stacked && !commonYAxis) {
		series.yAxis = seriesIndex;
	}

	if (facts) {
		const {xAxis} = chartSettings;

		if (xAxis && xAxis.categories) {
			series.data = createSeriesData(facts, xAxis.categories, groupBy);
		} else {
			facts.forEach((fact: AggregatedFact) => {
				const timestamp = dayjs.utc(fact.rangeStart).local();
				series.data.push([timestamp.valueOf(), fact.aggregatedValue || 0]);
			});
		}
	}

	return series;
};

export const updateChart = (
	chart: Highcharts.Chart,
	chartDataSets: ChartData[],
	showAssetNames: boolean,
	settings: KpiAggregatedChartWidgetConfigSettings,
	aggregationSettings: AggregationSettings,
	time: number | null | undefined,
	assets: Asset[]
) => {
	const {groupBy, stacked, commonYAxis} = settings;
	const dateFormat = getGroupBySettings(groupBy).dateFormat;
	const chartSettings = digestSettings(settings, aggregationSettings, dateFormat, dayjs(time));

	if (chart && chart.series && chart.xAxis) {
		// update the x-Axis since the time range might have changed due to time progress (switch from one hour to another)
		if (chartSettings.xAxis) {
			const {xAxis} = chart;
			const {
				xAxis: {categories, labels}
			} = chartSettings;
			if (xAxis && xAxis.length)
				xAxis[0].update(
					{
						categories: categories as string[],
						labels
					},
					false
				);
		}

		// update the chart data sets
		chartDataSets.forEach((dataSet, index) => {
			const {label, assetUrn, kpiName, facts, uuid} = dataSet;
			const seriesName =
				label ||
				(showAssetNames ? `${assets.find(({urn}) => urn === assetUrn)?.name || '--'} - ${kpiName}` : kpiName);
			const seriesToUpdateIndex = chart.series.findIndex(({name, userOptions: {id}}) =>
				id && uuid ? id === uuid : name === seriesName
			);

			if (seriesToUpdateIndex !== -1 && facts && chartSettings.xAxis && chartSettings.xAxis.categories) {
				// we need to update the full series since series.setData is not working due to category mapping
				// seems to be a bug in highchart
				const fullSeries = createSeries(
					dataSet,
					chartSettings,
					index,
					showAssetNames,
					stacked,
					commonYAxis,
					groupBy,
					assets
				);
				chart.series[seriesToUpdateIndex].update(fullSeries, false);
			} else {
				// add series, it's not there yet, which should never happen....
				console.warn('Series added afterwards...');
				chart.addSeries(
					createSeries(dataSet, chartSettings, index, showAssetNames, stacked, commonYAxis, groupBy, assets),
					false
				);
			}
		});
		chart.redraw();
	}
};
export const createChart = (
	domNode: any,
	chartDataSets: ChartData[],
	settings: KpiAggregatedChartWidgetConfigSettings,
	aggregationSettings: AggregationSettings,
	showAssetNames: boolean,
	time: number | null | undefined,
	assets: Asset[]
): any => {
	const {stacked, commonYAxis, groupBy} = settings;
	const dateFormat = getGroupBySettings(groupBy).dateFormat;
	const chartSettings = digestSettings(settings, aggregationSettings, dateFormat, dayjs(time));
	const series = chartDataSets.map((chartData, index) =>
		createSeries(chartData, chartSettings, index, showAssetNames, stacked, commonYAxis, groupBy, assets)
	);
	const chartOptions = buildChartOptions(groupBy, chartSettings, series, stacked, dateFormat);
	return Highcharts.chart(domNode, chartOptions as any);
};
